import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Button, Text } from 'v3/components';
import { cooperplaceApi } from 'services/api';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import Lightbox from 'components/Lightbox';
import { styles } from './styles';

function isParentQuestion(q) {
  return !!q && (!q.fatherQuestionId || q.hasSubQuestion);
}
function isSubQuestion(q) {
  return !!q?.fatherQuestionId;
}
function groupQuestionsWithSubQuestions(list = []) {
  const parents = list.filter((q) => isParentQuestion(q));
  const subsByParent = new Map();
  list.filter((q) => isSubQuestion(q)).forEach((sub) => {
    const arr = subsByParent.get(sub.fatherQuestionId) || [];
    arr.push(sub);
    subsByParent.set(sub.fatherQuestionId, arr);
  });
  return parents.map((p) => ({ parent: p, subQuestions: subsByParent.get(p.id) || [] }));
}

export default function AnswersModal({
  open = false,
  onClose,
  singleChecklistId = null,
  checklistTitle = '',
}) {
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [data, setData] = useState(null);
  const [responsesList, setResponsesList] = useState([]);
  const mountedRef = useRef(true);

  const [lightbox, setLightbox] = useState({
    isOpen: false,
    items: [],
    index: 0,
  });

  const [preview, setPreview] = useState({
    isOpen: false,
    url: '',
    name: '',
    kind: '',
    blobUrl: '',
    loading: false,
    error: '',
  });

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleCloseModal = useCallback(() => {
    setLightbox({ isOpen: false, items: [], index: 0 });
    if (typeof onClose === 'function') onClose();
  }, [onClose]);

  useEffect(() => {
    if (!open) {
      setLightbox({ isOpen: false, items: [], index: 0 });
      setPreview({ isOpen: false, url: '', name: '', kind: '' });
      setExportLoading(false);
    }
  }, [open]);

  useEffect(() => {
    function onEsc(e) {
      if (e.key !== 'Escape') return;

      if (lightbox.isOpen) {
        setLightbox((prev) => ({ ...prev, isOpen: false }));
        return;
      }

      if (preview.isOpen) {
        setPreview({ isOpen: false, url: '', name: '', kind: '' });
        return;
      }

      handleCloseModal();
    }
    if (open) document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [open, lightbox.isOpen, preview.isOpen, handleCloseModal]);

  function closePreview() {
    setPreview((p) => {
      if (p?.blobUrl) {
        try { URL.revokeObjectURL(p.blobUrl); } catch (_) { }
      }
      return { isOpen: false, url: '', name: '', kind: '', blobUrl: '', loading: false, error: '' };
    });
  }

  const formatDateTimeBR = useCallback((value) => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('pt-BR');
  }, []);

  const formatDateBR = useCallback((value) => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('pt-BR');
  }, []);

  function getAttachmentUrl(att) {
    return (
      att?.attachment?.full_path ||
      att?.attachment?.url ||
      att?.attachment?.publicUrl ||
      att?.attachment?.link ||
      att?.attachment?.path ||
      att?.url ||
      att?.link ||
      ''
    );
  }

  function getAttachmentName(att, i) {
    return (
      att?.attachment?.name ||
      att?.attachment?.filename ||
      att?.name ||
      att?.filename ||
      `Arquivo ${i + 1}`
    );
  }

  function flattenQuestionsFromResponse(r) {
    return Array.isArray(r?.questions) ? r.questions : [];
  }

  function isImageUrl(url) {
    const lower = String(url || '').toLowerCase();
    return (
      lower.includes('.png') ||
      lower.includes('.jpg') ||
      lower.includes('.jpeg') ||
      lower.includes('.webp')
    );
  }

  function isVideoUrl(url) {
    const lower = String(url || '').toLowerCase();
    return (
      lower.includes('.mp4') ||
      lower.includes('.webm') ||
      lower.includes('.mov') ||
      lower.includes('.m4v') ||
      lower.includes('.avi')
    );
  }

  function isPdfUrl(url) {
    const lower = String(url || '').toLowerCase();
    return lower.includes('.pdf');
  }

  async function openPreview(url, name) {
    if (!url) return;

    const kind = isPdfUrl(url) ? 'pdf' : isVideoUrl(url) ? 'video' : 'file';
    setPreview({
      isOpen: true,
      url,
      name,
      kind,
      blobUrl: '',
      loading: kind === 'pdf',
      error: '',
    });

    if (kind === 'pdf') {
      try {
        const resp = await cooperplaceApi.get(url, { responseType: 'blob' });
        const blobUrl = URL.createObjectURL(resp.data);
        setPreview((p) => ({ ...p, blobUrl, loading: false }));
      } catch (e) {
        setPreview((p) => ({
          ...p,
          loading: false,
          error: 'Não foi possível abrir o PDF para visualização.',
        }));
      }
    }
  }

  function countPhotosFromResponse(r) {
    const qs = flattenQuestionsFromResponse(r);
    let total = 0;
    qs.forEach((qItem) => {
      (qItem?.attachments || []).forEach((a) => {
        const url = getAttachmentUrl(a);
        if (!url) return;
        if (isImageUrl(url)) total += 1;
      });
    });
    return total;
  }

  function normalizeUrl(u) {
    return String(u || '').toLowerCase().split('?')[0].split('#')[0];
  }

  function isImageUrl(url) {
    const lower = normalizeUrl(url);
    return lower.endsWith('.png') || lower.endsWith('.jpg') || lower.endsWith('.jpeg') || lower.endsWith('.webp');
  }

  function isVideoUrl(url) {
    const lower = normalizeUrl(url);
    return lower.endsWith('.mp4') || lower.endsWith('.webm') || lower.endsWith('.mov') || lower.endsWith('.m4v') || lower.endsWith('.avi');
  }

  function isPdfUrl(url) {
    const lower = normalizeUrl(url);
    return lower.endsWith('.pdf');
  }

  function openLightboxFromAttachments({ attachments, currentUrl }) {
    const items = (attachments || [])
      .map((att, i) => {
        const url = getAttachmentUrl(att);
        const name = getAttachmentName(att, i);
        return { url, name };
      })
      .filter((x) => !!x.url && isImageUrl(x.url));

    if (!items.length) return;

    const idx = Math.max(0, items.findIndex((x) => x.url === currentUrl));
    setLightbox({
      isOpen: true,
      items,
      index: idx === -1 ? 0 : idx,
    });
  }

  function downloadFile(url, filename) {
    try {
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'arquivo';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (e) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  useEffect(() => {
    async function fetchParent() {
      if (!open || !singleChecklistId) return;

      try {
        setLoading(true);
        setData(null);
        setResponsesList([]);
        setLightbox({ isOpen: false, items: [], index: 0 });

        const resp = await cooperplaceApi.get(`/single-checklist/${singleChecklistId}`);
        if (!mountedRef.current) return;

        const payload = resp?.data || null;
        setData(payload);

        const list = Array.isArray(payload?.questionsResponses) ? payload.questionsResponses : [];
        setResponsesList(list);
      } catch (err) {
        if (!mountedRef.current) return;
        setData(null);
        setResponsesList([]);
        setLightbox({ isOpen: false, items: [], index: 0 });
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    }

    fetchParent();
  }, [open, singleChecklistId]);

  const downloadChecklist = async (singleChecklistId) => {
    setExportLoading(true);
    try {
      await cooperplaceApi
        .get(`single-checklist/export/${singleChecklistId}`, {
          responseType: 'blob',
          headers: {
            'Content-Type': 'application/pdf',
          },
        })
        .then(response => {
          const formattedTitle = checklistTitle?.replace(/\s+/g, '_');
          const fileName = `single_checklist_${formattedTitle}_${singleChecklistId}.pdf`;
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        });
    } catch (error) {
      setExportLoading(false);
      console.error('Error exporting checklist:', error);

    } finally {
      setExportLoading(false);
    }
  };

  const title = useMemo(() => checklistTitle || data?.name || 'Checklist', [checklistTitle, data]);
  const displayTitle = useMemo(() => {
    const currentId = data?.id || singleChecklistId;
    if (!currentId) return title;
    return `#${currentId} ${title}`;
  }, [data, singleChecklistId, title]);
  const createdBy = data?.createdUser?.username || '—';
  const createdAt = formatDateBR(data?.createdAt);
  const firstResponse = useMemo(() => responsesList?.[0] || null, [responsesList]);

  const questions = useMemo(() => {
    const list = firstResponse ? flattenQuestionsFromResponse(firstResponse) : [];
    return Array.isArray(list) ? groupQuestionsWithSubQuestions(list) : [];
  }, [firstResponse]);

  if (!open) return null;
  const isSingleQuestion = questions.length === 1;
  const userName = firstResponse?.user?.username || '—';
  const userEmail = firstResponse?.user?.email || '—';
  const dt = firstResponse?.answeredAt || null;
  const photos = firstResponse ? countPhotosFromResponse(firstResponse) : 0;
  const hasLightbox = lightbox.isOpen && (lightbox.items?.length || 0) > 0;
  const nextIndex = hasLightbox ? (lightbox.index + 1) % lightbox.items.length : 0;
  const prevIndex = hasLightbox ? (lightbox.index + lightbox.items.length - 1) % lightbox.items.length : 0;

  function MediaGrid({ attachments }) {
    const safeList = Array.isArray(attachments) ? attachments : [];
    const count = safeList.length;
    const cols = count <= 1 ? 1 : count === 2 ? 2 : 3;

    const tileH =
      count <= 2 ? 150 :
        count <= 6 ? 130 :
          count <= 12 ? 110 :
            count <= 24 ? 95 :
              80;

    const hasScroll = count >= 4;

    const maxH =
      count <= 6 ? (tileH * 2) + 32 :
        330;

    return (
      <div
        style={{
          ...styles.mediaGridWrap,
          overflow: hasScroll ? 'auto' : 'visible',
          maxHeight: hasScroll ? maxH : 'none',
          paddingRight: hasScroll ? 2 : 0,
        }}
      >
        <div
          style={{
            ...styles.mediaGrid,
            gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
          }}
        >
          {safeList.map((a, i) => (
            <MediaTile
              key={i}
              att={a}
              index={i}
              attachments={safeList}
              tileHeight={tileH}
            />
          ))}
        </div>
      </div>
    );
  }


  function MediaTile({ att, index, attachments, tileHeight }) {
    const [hover, setHover] = useState(false);

    const url = getAttachmentUrl(att);
    const name = getAttachmentName(att, index);

    if (!url) {
      return (
        <div style={styles.mediaDocTile} title={name}>
          <div style={styles.mediaDocIconWrap}>
            <InsertDriveFileOutlinedIcon style={{ fontSize: 22, color: '#6B7280' }} />
          </div>
          <div style={styles.mediaName}>{name}</div>
        </div>
      );
    }

    const img = isImageUrl(url);
    const vid = isVideoUrl(url);
    const pdf = isPdfUrl(url);

    if (img) {
      return (
        <button
          type="button"
          style={{
            ...styles.mediaImgTile,
            height: tileHeight,
            backgroundImage: `url(${url})`,
          }}
          title="Visualizar"
          onClick={() => openLightboxFromAttachments({ attachments, currentUrl: url })}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
        >
          <div style={{ ...styles.mediaOverlay, opacity: hover ? 1 : 0 }}>
            <div style={styles.mediaOverlayBtn} aria-hidden="true">
              <SearchOutlinedIcon style={{ fontSize: 22, color: '#fff' }} />
            </div>
          </div>

          <div style={styles.mediaNameBelow} title={name}>
            {name}
          </div>
        </button>
      );
    }

    if (pdf) {
      return (
        <button
          type="button"
          style={{
            ...styles.mediaImgTile,
            height: tileHeight,
            background: '#F3F4F6',
            border: '1px solid #E5E7EB',
            position: 'relative',
          }}
          title="Abrir PDF"
          onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
        >
          <div style={styles.mediaDocIconWrap}>
            <InsertDriveFileOutlinedIcon style={{ fontSize: 32, color: '#6B7280' }} />
          </div>

          <div style={{ ...styles.mediaOverlay, opacity: hover ? 1 : 0 }}>
            <div style={styles.mediaOverlayBtn}>
              <VisibilityOutlinedIcon style={{ fontSize: 22, color: '#fff' }} />
            </div>
          </div>

          <div style={styles.mediaNameBelow} title={name}>
            {name}
          </div>
        </button>
      );
    }
    if (vid) {
      return (
        <button
          type="button"
          style={{
            ...styles.mediaImgTile,
            height: tileHeight,
            padding: 0,
            backgroundImage: 'none',
            overflow: 'hidden',
            position: 'relative',
          }}
          title="Visualizar vídeo"
          onClick={() => openPreview(url, name)}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
        >
          <video
            src={url}
            muted
            playsInline
            preload="metadata"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              background: '#111827',
              display: 'block',
            }}
            onLoadedMetadata={(e) => {
              try {
                e.currentTarget.currentTime = 0.1;
              } catch (_) { }
            }}
          />
          <div style={{ ...styles.mediaOverlay, opacity: hover ? 1 : 0 }}>
            <div style={styles.mediaOverlayBtn} aria-hidden="true">
              <SearchOutlinedIcon style={{ fontSize: 22, color: '#fff' }} />
            </div>
          </div>

          <div
            style={{
              position: 'absolute',
              left: 10,
              top: 10,
              background: 'rgba(17,24,39,0.70)',
              color: '#fff',
              fontSize: 11,
              fontWeight: 800,
              padding: '4px 8px',
              borderRadius: 999,
            }}
          >
            VÍDEO
          </div>

          <div style={styles.mediaNameBelow} title={name}>
            {name}
          </div>
        </button>
      );
    }

    return (
      <button
        type="button"
        style={styles.mediaDocBtn}
        title="Abrir arquivo"
        onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
      >
        <div style={styles.mediaDocIconWrap}>
          <InsertDriveFileOutlinedIcon style={{ fontSize: 22, color: '#6B7280' }} />
        </div>
        <div style={styles.mediaName}>{name}</div>
      </button>
    );
  }

  return (
    <div
      style={styles.backdrop}
      onMouseDown={(e) => {
        if (lightbox.isOpen) return;
        if (e.target === e.currentTarget) handleCloseModal();
      }}
    >
      {hasLightbox && (
        <Lightbox
          mainSrc={lightbox.items[lightbox.index].url}
          nextSrc={lightbox.items[nextIndex].url}
          prevSrc={lightbox.items[prevIndex].url}
          onCloseRequest={() => setLightbox((p) => ({ ...p, isOpen: false }))}
          onMovePrevRequest={() => setLightbox((p) => ({ ...p, index: prevIndex }))}
          onMoveNextRequest={() => setLightbox((p) => ({ ...p, index: nextIndex }))}
          imageTitle={lightbox.items[lightbox.index].name}
          toolbarButtons={[
            <button
              key="download"
              type="button"
              onClick={() => {
                const item = lightbox.items[lightbox.index];
                downloadFile(item.url, item.name);
              }}
              style={styles.lightboxIconBtn}
              title="Baixar"
              aria-label="Baixar"
            >
              <DownloadOutlinedIcon style={{ color: '#fff', fontSize: 22 }} />
            </button>,
          ]}
          reactModalStyle={{
            overlay: { zIndex: 10000 },
          }}
        />
      )}

      {preview.isOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10001,
            background: 'rgba(17, 24, 39, 0.65)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 14,
          }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closePreview();
          }}
        >
          <div
            style={{
              width: 'min(980px, 100%)',
              height: 'min(88vh, 760px)',
              background: '#fff',
              borderRadius: 14,
              border: '1px solid #E5E7EB',
              boxShadow: '0 28px 80px rgba(0,0,0,0.22)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
            onMouseDown={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div
              style={{
                padding: '12px 14px',
                borderBottom: '1px solid #E5E7EB',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontWeight: 800,
                    fontSize: 13,
                    color: '#111827',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: 700,
                  }}
                  title={preview.name}
                >
                  {preview.name || 'Arquivo'}
                </div>

                <div style={{ fontSize: 12, color: '#6B7280' }}>
                  {preview.kind === 'pdf'
                    ? 'Visualização de PDF'
                    : preview.kind === 'video'
                      ? 'Visualização de vídeo'
                      : 'Visualização de arquivo'}
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  type="button"
                  style={styles.lightboxIconBtn}
                  title="Baixar"
                  onClick={() => downloadFile(preview.url, preview.name)}
                >
                  <DownloadOutlinedIcon style={{ color: '#111827', fontSize: 20 }} />
                </button>

                <button
                  type="button"
                  style={styles.lightboxIconBtn}
                  title="Abrir em nova aba"
                  onClick={() => window.open(preview.url, '_blank', 'noopener,noreferrer')}
                >
                  <VisibilityOutlinedIcon style={{ color: '#111827', fontSize: 20 }} />
                </button>

                <button
                  type="button"
                  onClick={closePreview}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    fontSize: 20,
                    lineHeight: 1,
                    color: '#111827',
                    padding: 6,
                  }}
                  aria-label="Fechar preview"
                  title="Fechar"
                >
                  ✕
                </button>
              </div>
            </div>
            <div style={{ flex: 1, background: '#111827' }}>
              {preview.loading ? (
                <div style={{ padding: 16 }}>
                  <Text color="gray">Carregando...</Text>
                </div>
              ) : preview.error ? (
                <div style={{ padding: 16, background: '#F9FAFB', height: '100%' }}>
                  <Text color="danger" weight={700}>
                    {preview.error}
                  </Text>

                  <div style={{ marginTop: 10, display: 'flex', gap: 10 }}>
                    <Button
                      variant="secondary"
                      onClick={() => window.open(preview.url, '_blank', 'noopener,noreferrer')}
                    >
                      Abrir em nova aba
                    </Button>

                    <Button onClick={() => downloadFile(preview.url, preview.name)}>Baixar</Button>
                  </div>
                </div>
              ) : preview.kind === 'pdf' ? (
                <iframe
                  title={preview.name || 'PDF'}
                  src={preview.blobUrl || preview.url}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 0,
                    background: '#F9FAFB',
                  }}
                />
              ) : preview.kind === 'video' ? (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#111827',
                    overflow: 'hidden',
                  }}
                >
                  <video
                    src={preview.url}
                    controls
                    playsInline
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      width: 'auto',
                      height: 'auto',
                      display: 'block',
                      background: '#111827',
                    }}
                  />
                </div>
              ) : (
                <div style={{ padding: 16, background: '#F9FAFB', height: '100%' }}>
                  <Text color="gray">Não é possível pré-visualizar este tipo de arquivo aqui.</Text>

                  <div style={{ marginTop: 10, display: 'flex', gap: 10 }}>
                    <Button
                      variant="secondary"
                      onClick={() => window.open(preview.url, '_blank', 'noopener,noreferrer')}
                    >
                      Abrir em nova aba
                    </Button>
                    <Button onClick={() => downloadFile(preview.url, preview.name)}>Baixar</Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div
        style={styles.modal}
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <div style={styles.titleLine}>
              <Text type="header" style={{ fontWeight: 800 }}>
                {displayTitle}
              </Text>
            </div>

            <div style={styles.metaLine}>
              <span style={styles.metaItem}>
                <PersonOutlineOutlinedIcon style={styles.metaIcon} />
                Criado por: <strong style={styles.metaStrong}>{createdBy}</strong>
              </span>
              <span style={styles.metaItem}>
                <CalendarTodayOutlinedIcon style={styles.metaIcon} />
                {createdAt}
              </span>
              <span style={styles.metaItem}>
                <ImageOutlinedIcon style={styles.metaIcon} />
                Resposta
              </span>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
            <button
              type="button"
              onClick={() => downloadChecklist(singleChecklistId)}
              disabled={exportLoading || singleChecklistId === null}
              style={{
                ...styles.exportBtn,
                opacity: exportLoading ? 0.5 : 1,
                cursor: exportLoading ? 'not-allowed' : 'pointer',
              }}
              onMouseEnter={(e) => {
                if (!exportLoading) {
                  e.currentTarget.style.background = '#E5E7EB';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#F3F4F6';
              }}
            >
              <DownloadOutlinedIcon style={{ fontSize: 18 }} />
              {exportLoading ? 'Exportando...' : 'Exportar'}
            </button>

            <button
              type="button"
              onClick={handleCloseModal}
              style={styles.closeBtn}
              aria-label="Fechar"
              disabled={exportLoading}
            >
              <CloseOutlinedIcon style={{ fontSize: 22, color: '#6B7280' }} />
            </button>
          </div>
        </div>

        <div style={styles.content}>
          {loading && (
            <div style={styles.center}>
              <Text type="regular" color="gray">
                Carregando respostas...
              </Text>
            </div>
          )}

          {!loading && !data && (
            <div style={styles.center}>
              <Text type="regular" color="gray">
                Não foi possível carregar as respostas.
              </Text>
            </div>
          )}

          {!loading && !!data && (
            <>
              {!firstResponse ? (
                <div style={styles.center}>
                  <Text type="regular" color="gray">
                    Nenhuma resposta encontrada para este checklist.
                  </Text>
                </div>
              ) : (
                <>
                  <div style={styles.singleHeader}>
                    <div style={{ minWidth: 0 }}>
                      <div style={styles.qTitle}>Respondido por:</div>
                      <div style={styles.qSub}>
                        👤 {userName} • Email: {userEmail}
                      </div>
                      <div style={styles.qSub}>
                        🗓 {dt ? formatDateTimeBR(dt) : '—'} • 📷 {photos} foto
                        {photos === 1 ? '' : 's'}
                      </div>
                    </div>
                  </div>
                  <div style={isSingleQuestion ? styles.questionsGridSingle : styles.questionsGrid}>
                    {questions.map(({ parent, subQuestions }, index) => {
                      const selectedValues = (parent?.responses || []).filter((x) => x?.selected);
                      const hasSelectedLocal = selectedValues.length > 0;
                      const slug = String(parent?.type?.slug || '').toLowerCase();
                      const rawDescriptive = parent?.questionDescriptiveResponse;
                      const plateValue =
                        parent?.vehicle?.plate ||
                        parent?.wagon?.plate ||
                        '';
                      const codeValue = parent?.code || '';
                      const descriptive =
                        rawDescriptive || plateValue || (slug === 'code' || slug === 'code_reader' ? codeValue : '');
                      const attachments = parent?.attachments || [];
                      const number = index + 1;

                      return (
                        <div key={parent?.id || `${index}`} style={styles.card}>
                          <div style={styles.questionTitleRow}>
                            <span style={styles.questionNumber}>{number}</span>
                            <div style={styles.questionTitleText}>{parent?.question || '—'}</div>
                          </div>
                          <div style={styles.questionDivider} />

                          {hasSelectedLocal && selectedValues.length === 1 && (
                            <div style={{ marginTop: 10 }}>
                              <div style={styles.answerRow}>
                                {selectedValues[0]?.description || '—'}
                              </div>
                            </div>
                          )}

                          {!!descriptive && (
                            <div style={{ marginTop: 10 }}>
                              {
                                !plateValue && (
                                  <div style={styles.sectionTitle}>Observação</div>
                                )
                              }
                              <div style={styles.textBox}>{descriptive}</div>
                            </div>
                          )}

                          {!!attachments.length && (
                            <div style={{ marginTop: 10 }}>
                              <div style={styles.sectionTitle}>Arquivos</div>

                              <MediaGrid attachments={attachments} />
                            </div>
                          )}

                          {!hasSelectedLocal && !descriptive && !attachments.length && (
                            <div style={{ marginTop: 10 }}>
                              <Text type="regular" color="gray">
                                Sem resposta registrada.
                              </Text>
                            </div>
                          )}

                          {!!subQuestions.length && (
                            <div style={{ marginTop: 10, paddingLeft: 10, borderLeft: '2px solid #E5E7EB', display: 'flex', flexDirection: 'column', gap: 10 }}>
                              {subQuestions.map((sub, si) => {
                                const subSelected = (sub?.responses || []).filter((x) => x?.selected);
                                const subHasSelected = subSelected.length > 0;
                                const subSlug = String(sub?.type?.slug || '').toLowerCase();
                                const subRawDesc = sub?.questionDescriptiveResponse;
                                const subCode = sub?.code || '';
                                const subPlate = sub?.vehicle?.plate || sub?.wagon?.plate || '';
                                const subDesc =
                                  subRawDesc ||
                                  subPlate ||
                                  (subSlug === 'code' || subSlug === 'code_reader' ? subCode : '');
                                const subAtt = sub?.attachments || [];

                                const hasContent = subHasSelected || subDesc || subAtt.length;

                                if (!hasContent) return null;

                                return (
                                  <div key={sub?.id || `${parent?.id}-sub-${si}`} style={{ ...styles.card, padding: 10, border: '1px dashed #E5E7EB' }}>
                                    <div style={styles.questionTitleRow}>
                                      <span style={styles.questionNumber}>{`${number}.${si + 1}`}</span>
                                      <div style={styles.questionTitleText}>{sub?.question || '—'}</div>
                                    </div>
                                    <div style={styles.questionDivider} />

                                    {subHasSelected && subSelected.length === 1 && (
                                      <div style={{ marginTop: 10 }}>
                                        <div style={styles.answerRow}>{subSelected[0]?.description || '—'}</div>
                                      </div>
                                    )}

                                    {!!subDesc && (
                                      <div style={{ marginTop: 10 }}>
                                        <div style={styles.sectionTitle}>Observação</div>
                                        <div style={styles.textBox}>{subDesc}</div>
                                      </div>
                                    )}

                                    {!!subAtt.length && (
                                      <div style={{ marginTop: 10 }}>
                                        <div style={styles.sectionTitle}>Arquivos</div>
                                        <MediaGrid attachments={subAtt} />
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          )}
        </div>
        <div style={styles.footer}>
          <Button variant="secondary" onClick={handleCloseModal} disabled={exportLoading}>
            Fechar
          </Button>
        </div>
      </div>
    </div>
  );
}
